import type { TaskFull } from '@brain/contracts'
import { Button, Card, Chip, EmptyState, ListRow, PulseSheet, toast } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CalendarDays, CalendarPlus, CheckCircle2, FileText, Inbox, RefreshCw, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CaptureBar } from '../../components/CaptureBar.tsx'
import { relativeDay } from '../../components/NoteList.tsx'
import { Timeline } from '../../components/today/Timeline.tsx'
import { BriefingText, EveningReviewCard, EventDialog } from '../../components/today/TodayParts.tsx'
import { dayPart, firstName, formatLongDate } from '../../lib/greeting.ts'
import { inboxQuery, notesQuery } from '../../lib/hooks.ts'
import { useUpdateTask } from '../../lib/m2.ts'
import { todayQuery, useAcceptPlan, useGeneratePlan, useUpdatePlan } from '../../lib/today.ts'

interface TodaySearch {
  /** calendar event (cache id) to show – from the briefing's "Vorbereiten" or a push */
  prep?: string
  event?: string
  /** evening review push */
  review?: string
}

export const Route = createFileRoute('/_app/')({
  validateSearch: (s: Record<string, unknown>): TodaySearch => ({
    ...(typeof s.prep === 'string' ? { prep: s.prep } : {}),
    ...(typeof s.event === 'string' ? { event: s.event } : {}),
    ...(typeof s.review === 'string' ? { review: s.review } : {}),
  }),
  component: Today,
})

function Today() {
  const { t } = useTranslation()
  const { me } = Route.useRouteContext()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/' })
  const now = new Date()
  const tz = me.timezone || 'Europe/Berlin'
  const part = dayPart(now, tz)
  const name = firstName(me.name)
  const today = useQuery(todayQuery())
  const inbox = useQuery(inboxQuery())
  const recent = useQuery(notesQuery({ limit: 6, sort: 'updated' }))
  const generate = useGeneratePlan()
  const accept = useAcceptPlan()
  const v = today.data
  const plan = v?.plan ?? null
  const update = useUpdatePlan(v?.date ?? '')
  const inboxCount = inbox.data?.length ?? 0
  const openEvent = search.prep ?? search.event ?? null
  const showReview = Boolean(plan && (v?.reviewDue || search.review))

  const doGenerate = () =>
    v &&
    generate.mutate(v.date, {
      onSuccess: () => toast(plan ? t('today.replanned') : t('today.planned')),
      onError: () => toast(t('common.error'), { tone: 'attention' }),
    })
  const doAccept = () =>
    v &&
    accept.mutate(v.date, {
      onSuccess: (r) =>
        toast(r.calendar ? t('today.acceptedCalendar', { count: r.written }) : t('today.acceptedNoCalendar')),
      onError: () => toast(t('common.error'), { tone: 'attention' }),
    })

  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div data-tauri-drag-region className="hidden h-6 md:block" />
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-4 md:px-6">
        <PulseSheet className="px-6 py-7 md:px-8">
          <p className="meta-label">{formatLongDate(now, 'de-DE', tz)}</p>
          <h1 className="mt-2 text-[26px] text-text-1 leading-tight">
            {name ? t(`greeting.${part}`, { name }) : t(`greeting.${part}Anon`)}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {plan && (
              <Chip tone={plan.status === 'accepted' ? 'green' : 'apricot'}>
                {t(`today.status.${plan.status}`)}
              </Chip>
            )}
            {plan && plan.status !== 'accepted' && (
              <Button onClick={doAccept} disabled={accept.isPending}>
                <CheckCircle2 size={16} /> {t('today.accept')}
              </Button>
            )}
            <Button
              variant={plan ? 'secondary' : 'primary'}
              onClick={doGenerate}
              disabled={generate.isPending || !v}
            >
              {plan ? <RefreshCw size={16} /> : <Sparkles size={16} />}
              {plan ? t('today.replan') : t('today.planDay')}
            </Button>
            {v && !v.calendar.connected && v.calendar.available && (
              <Link to="/einstellungen" className="inline-flex items-center gap-1 text-[13px] text-pop-fg">
                <CalendarPlus size={14} /> {t('today.connectCalendar')}
              </Link>
            )}
            {v?.calendar.error && (
              <span className="text-[12px] text-apricot-fg">{t('today.calendarError')}</span>
            )}
          </div>
          {plan?.rationaleMd && (
            <p className="mt-3 max-w-2xl text-[13px] text-text-2">{plan.rationaleMd.split('\n')[0]}</p>
          )}
        </PulseSheet>
        <CaptureBar />

        {showReview && plan && (
          <Card className="p-2">
            <h2 className="px-4 pt-3 meta-label">{t('today.review')}</h2>
            <EveningReviewCard plan={plan} onDone={() => void navigate({ search: {} })} />
          </Card>
        )}

        <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
          <Card className="p-3">
            <h2 className="px-1 pb-2 meta-label">{t('today.timeline')}</h2>
            {v && (v.events.length > 0 || (plan?.blocks.length ?? 0) > 0) ? (
              <Timeline
                events={v.events}
                blocks={plan?.blocks ?? []}
                editable={Boolean(plan)}
                showNow
                onMove={(id, start, end) => update.mutate({ moved: [{ id, start, end }] })}
                onRemove={(id) => update.mutate({ removed: [id] })}
                onOpenEvent={(id) => void navigate({ search: { event: id } })}
              />
            ) : (
              <EmptyState
                icon={<CalendarDays size={20} strokeWidth={1.9} />}
                title={t('today.timeline')}
                description={v?.calendar.connected ? t('today.timelineNoPlan') : t('today.timelineEmpty')}
              />
            )}
          </Card>
          <div className="flex flex-col gap-4">
            {plan?.briefingMd && (
              <Card className="p-4">
                <BriefingText markdown={plan.briefingMd} />
                {plan.rationaleMd.includes('\n') && (
                  <div className="mt-3 border-line-1 border-t pt-3">
                    <BriefingText markdown={plan.rationaleMd.split('\n').slice(1).join('\n')} />
                  </div>
                )}
              </Card>
            )}
            <TaskCard title={t('today.focus')} tasks={v?.focusTasks ?? []} empty={t('today.top3Empty')} />
            {(v?.overdue.length ?? 0) > 0 && <TaskCard title={t('today.overdue')} tasks={v?.overdue ?? []} />}
            {(v?.commitmentsDue.length ?? 0) > 0 && (
              <Card className="p-2">
                <h2 className="px-4 pt-3 pb-1 meta-label">{t('today.commitments')}</h2>
                {v?.commitmentsDue.map((c) => (
                  <ListRow
                    key={c.id}
                    title={c.text}
                    trailing={
                      <span className="text-[11px] text-meta">{c.dueDate ? relativeDay(c.dueDate) : ''}</span>
                    }
                  />
                ))}
              </Card>
            )}
            <Card className="p-2">
              <h2 className="px-4 pt-3 meta-label">{t('today.approvals')}</h2>
              {inboxCount > 0 ? (
                <Link
                  to="/inbox"
                  className="m-2 flex items-center gap-3 rounded-tile border-apricot border-l-[3px] bg-apricot-bg px-3 py-2.5 text-[14px] text-apricot-fg"
                >
                  <Inbox size={16} strokeWidth={1.9} />
                  {t('today.inboxPending', { count: inboxCount })}
                </Link>
              ) : (
                <EmptyState
                  icon={<CheckCircle2 size={20} strokeWidth={1.9} />}
                  title={t('today.approvalsEmpty')}
                />
              )}
            </Card>
            <Card className="p-2">
              <h2 className="px-4 pt-3 pb-1 meta-label">{t('today.recent')}</h2>
              {(recent.data?.items ?? []).length === 0 && (
                <p className="px-4 py-3 text-[13px] text-meta">{t('note.emptyList')}</p>
              )}
              {(recent.data?.items ?? []).map((n) => (
                <Link key={n.id} to="/n/$noteId" params={{ noteId: n.id }} className="block">
                  <ListRow
                    leading={<FileText size={16} strokeWidth={1.9} />}
                    title={n.title || t('note.untitled')}
                    trailing={<span className="text-[11px] text-meta">{relativeDay(n.updatedAt)}</span>}
                  />
                </Link>
              ))}
            </Card>
          </div>
        </div>
      </div>
      <EventDialog id={openEvent} onClose={() => void navigate({ search: {} })} />
    </div>
  )
}

function TaskCard({ title, tasks, empty }: { title: string; tasks: TaskFull[]; empty?: string }) {
  const { t } = useTranslation()
  const update = useUpdateTask()
  return (
    <Card className="p-2">
      <h2 className="px-4 pt-3 pb-1 meta-label">{title}</h2>
      {tasks.length === 0 && empty && <p className="px-4 py-3 text-[13px] text-text-2">{empty}</p>}
      {tasks.slice(0, 8).map((task) => (
        <div key={task.id} className="flex items-center gap-2 px-3 py-1.5">
          <input
            type="checkbox"
            aria-label={t('today.complete', { title: task.title })}
            checked={task.status === 'done'}
            onChange={() =>
              update.mutate({ id: task.id, patch: { status: task.status === 'done' ? 'todo' : 'done' } })
            }
          />
          <Link
            to="/tasks"
            search={{ task: task.id }}
            className="min-w-0 flex-1 truncate text-[14px] text-text-1 hover:underline"
          >
            {task.title}
          </Link>
          {task.dueDate && (
            <span className="shrink-0 text-[11px] text-meta">{relativeDay(task.dueDate)}</span>
          )}
        </div>
      ))}
    </Card>
  )
}
